import { useState, useMemo, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import SASidebar from '@/pages/sa/dashboard/components/SASidebar';
import AdminImageUpload from '@/components/base/AdminImageUpload';
import { sponsors, sponsorMetrics, type Sponsor, type SponsorType, type SponsorStatus } from '@/mocks/sponsors';

type LoadingState = 'loading' | 'ready' | 'error';
type SponsorLevel = 'oro' | 'plata' | 'bronce';

const LEVEL_LABELS: Record<SponsorLevel, string> = { oro: 'Oro', plata: 'Plata', bronce: 'Bronce' };
const LEVEL_COLORS: Record<SponsorLevel, { bg: string; text: string }> = {
  oro: { bg: 'bg-amber-50', text: 'text-amber-700' },
  plata: { bg: 'bg-slate-50', text: 'text-slate-600' },
  bronce: { bg: 'bg-amber-100', text: 'text-amber-800' },
};

interface SponsorFormData {
  companyName: string;
  type: SponsorType;
  sector: string;
  level: SponsorLevel;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  city: string;
  country: string;
  websiteUrl: string;
  logoUrl: string;
  assignedRadio: string;
  startDate: string;
  endDate: string;
}

const emptyForm: SponsorFormData = {
  companyName: '',
  type: 'local',
  sector: '',
  level: 'bronce',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  city: '',
  country: '',
  websiteUrl: '',
  logoUrl: '',
  assignedRadio: '',
  startDate: '',
  endDate: '',
};

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function formatCurrency(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toLocaleString()}`;
}

function simulateLoad<T>(data: T): Promise<T> {
  return new Promise((resolve, reject) => {
    const d = 600 + Math.random() * 400;
    setTimeout(() => {
      if (Math.random() > 0.96) {
        reject(new Error('Timeout al conectar con el servidor.'));
      } else {
        resolve(data);
      }
    }, d);
  });
}

/* ── Status Badge ── */
const statusBadge = (s: SponsorStatus | 'archivado') => {
  const map: Record<string, { bg: string; text: string; dot: string; label: string }> = {
    activo: { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500', label: 'Activo' },
    inactivo: { bg: 'bg-foreground-100', text: 'text-foreground-500', dot: 'bg-foreground-400', label: 'Inactivo' },
    pendiente: { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500', label: 'Pendiente' },
    archivado: { bg: 'bg-red-50', text: 'text-red-500', dot: 'bg-red-400', label: 'Archivado' },
  };
  const st = map[s] || map.pendiente;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${st.bg} ${st.text} text-[10px] font-medium whitespace-nowrap`}>
      <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`}></span>
      {st.label}
    </span>
  );
};

/* ── Type Badge ── */
const typeBadge = (t: SponsorType) => {
  const map: Record<SponsorType, { bg: string; text: string; icon: string }> = {
    local: { bg: 'bg-sky-50', text: 'text-sky-600', icon: 'ri-map-pin-line' },
    regional: { bg: 'bg-violet-50', text: 'text-violet-600', icon: 'ri-road-map-line' },
    nacional: { bg: 'bg-accent-50', text: 'text-accent-600', icon: 'ri-global-line' },
  };
  const st = map[t];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${st.bg} ${st.text} text-[10px] font-medium`}>
      <i className={`${st.icon} text-[9px]`}></i>
      {t.charAt(0).toUpperCase() + t.slice(1)}
    </span>
  );
};

const sectorIcons: Record<string, string> = {
  'Financiero': 'ri-bank-line',
  'Automotriz': 'ri-car-line',
  'Gastronomía': 'ri-restaurant-line',
  'Salud y Fitness': 'ri-heart-pulse-line',
  'Tecnología': 'ri-smartphone-line',
  'Turismo': 'ri-plane-line',
  'Alimentación': 'ri-cake-2-line',
  'Educación': 'ri-book-open-line',
  'Comercio': 'ri-store-2-line',
  'Entretenimiento': 'ri-movie-line',
  'Salud': 'ri-heart-pulse-line',
  'Cultura': 'ri-palette-line',
};

function KpiCard({ label, value, icon, bg, color }: { label: string; value: string; icon: string; bg: string; color: string }) {
  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl p-4 flex items-center gap-3 hover:border-background-300 transition-colors">
      <div className={`w-10 h-10 rounded-xl ${bg} flex items-center justify-center shrink-0`}>
        <i className={`${icon} ${color} text-lg`}></i>
      </div>
      <div className="min-w-0">
        <p className="text-foreground-400 text-[10px] font-medium truncate">{label}</p>
        <p className="text-foreground-800 text-lg font-bold leading-tight">{value}</p>
      </div>
    </div>
  );
}

function TypeChip({ type, active, onClick, count }: {
  type: SponsorType | 'todos'; active: SponsorType | 'todos'; onClick: (s: SponsorType | 'todos') => void; count: number;
}) {
  const isActive = active === type;
  const labels: Record<string, string> = { todos: 'Todos', local: 'Local', regional: 'Regional', nacional: 'Nacional' };
  return (
    <button
      onClick={() => onClick(type)}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
        isActive
          ? 'bg-foreground-950 text-background-50'
          : 'bg-background-50 text-foreground-500 border border-background-200 hover:border-foreground-300 hover:text-foreground-700'
      }`}
    >
      {labels[type]}
      <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold ${isActive ? 'bg-background-50/20 text-background-50' : 'bg-background-100 text-foreground-400'}`}>{count}</span>
    </button>
  );
}

/* ── SweetAlert confirm helper ── */
function swalConfirm(title: string, text: string, confirmLabel: string, confirmColor: string = '#1a1a1a'): Promise<boolean> {
  return Swal.fire({
    icon: 'warning',
    title,
    text,
    showCancelButton: true,
    confirmButtonText: confirmLabel,
    cancelButtonText: 'Cancelar',
    confirmButtonColor: confirmColor,
    cancelButtonColor: '#e5e5e5',
    customClass: { popup: 'rounded-2xl text-sm', title: 'text-sm font-bold', confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl', cancelButton: 'text-xs font-medium px-4 py-2 rounded-xl text-foreground-700' },
  }).then(r => r.isConfirmed);
}

/* ═══════════════════════════════════════════════
   CREATE SPONSOR MODAL
   ═══════════════════════════════════════════════ */
function CreateSponsorModal({ onClose, onSave }: { onClose: () => void; onSave: (data: SponsorFormData) => void }) {
  const [form, setForm] = useState<SponsorFormData>({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  const update = (field: keyof SponsorFormData, value: string) => setForm(p => ({ ...p, [field]: value }));

  const handleSubmit = () => {
    if (!form.companyName.trim() || !form.contactEmail.trim()) {
      Swal.fire({ icon: 'warning', title: 'Campos requeridos', text: 'Empresa y Email son obligatorios.', confirmButtonColor: '#1a1a1a', customClass: { popup: 'rounded-2xl text-sm' } });
      return;
    }
    setSaving(true);
    setTimeout(() => { onSave(form); setSaving(false); onClose(); }, 600);
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative bg-background-50 rounded-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto shadow-lg">
        <div className="sticky top-0 bg-background-50 border-b border-background-200 p-5 flex items-center justify-between gap-4 z-10">
          <div>
            <h3 className="text-foreground-950 font-bold text-sm">Nuevo Patrocinador</h3>
            <p className="text-foreground-400 text-[10px] mt-0.5">Completá los datos del patrocinador</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center shrink-0 cursor-pointer hover:bg-background-200 transition-colors">
            <i className="ri-close-line text-foreground-500 text-sm"></i>
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-foreground-500 text-[10px] font-semibold uppercase tracking-wide block mb-1">Empresa *</label>
            <input type="text" value={form.companyName} onChange={e => update('companyName', e.target.value)} placeholder="Nombre de la empresa" className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-xl text-foreground-800 outline-none focus:border-foreground-300 transition-colors" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-foreground-500 text-[10px] font-semibold uppercase tracking-wide block mb-1">Tipo</label>
              <select value={form.type} onChange={e => update('type', e.target.value)} className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-xl text-foreground-800 outline-none focus:border-foreground-300 cursor-pointer">
                <option value="local">Local</option>
                <option value="regional">Regional</option>
                <option value="nacional">Nacional</option>
              </select>
            </div>
            <div>
              <label className="text-foreground-500 text-[10px] font-semibold uppercase tracking-wide block mb-1">Nivel</label>
              <select value={form.level} onChange={e => update('level', e.target.value as SponsorLevel)} className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-xl text-foreground-800 outline-none focus:border-foreground-300 cursor-pointer">
                <option value="oro">Oro</option>
                <option value="plata">Plata</option>
                <option value="bronce">Bronce</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-foreground-500 text-[10px] font-semibold uppercase tracking-wide block mb-1">Sector</label>
            <input type="text" value={form.sector} onChange={e => update('sector', e.target.value)} placeholder="Ej: Financiero, Tecnología..." className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-xl text-foreground-800 outline-none focus:border-foreground-300 transition-colors" />
          </div>
          <div>
            <AdminImageUpload
              label="Logo"
              currentImage={logoPreview}
              onChange={(previewUrl) => {
                setLogoPreview(previewUrl);
                update('logoUrl', previewUrl);
              }}
              onRemove={() => {
                setLogoPreview(null);
                update('logoUrl', '');
              }}
              previewHeight="h-16"
              previewWidth="w-16"
              placeholderIcon="ri-building-4-line"
            />
          </div>
          <div>
            <label className="text-foreground-500 text-[10px] font-semibold uppercase tracking-wide block mb-1">Sitio Web</label>
            <input type="url" value={form.websiteUrl} onChange={e => update('websiteUrl', e.target.value)} placeholder="https://..." className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-xl text-foreground-800 outline-none focus:border-foreground-300 transition-colors" />
          </div>
          <div className="border-t border-background-200 pt-4">
            <p className="text-foreground-500 text-[10px] font-semibold uppercase tracking-wide mb-3">Contacto</p>
            <div className="space-y-3">
              <div>
                <label className="text-foreground-400 text-[9px] block mb-1">Nombre</label>
                <input type="text" value={form.contactName} onChange={e => update('contactName', e.target.value)} className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-xl text-foreground-800 outline-none focus:border-foreground-300 transition-colors" />
              </div>
              <div>
                <label className="text-foreground-400 text-[9px] block mb-1">Email *</label>
                <input type="email" value={form.contactEmail} onChange={e => update('contactEmail', e.target.value)} className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-xl text-foreground-800 outline-none focus:border-foreground-300 transition-colors" />
              </div>
              <div>
                <label className="text-foreground-400 text-[9px] block mb-1">Teléfono</label>
                <input type="text" value={form.contactPhone} onChange={e => update('contactPhone', e.target.value)} className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-xl text-foreground-800 outline-none focus:border-foreground-300 transition-colors" />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-foreground-400 text-[9px] block mb-1">Ciudad</label>
              <input type="text" value={form.city} onChange={e => update('city', e.target.value)} className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-xl text-foreground-800 outline-none focus:border-foreground-300 transition-colors" />
            </div>
            <div>
              <label className="text-foreground-400 text-[9px] block mb-1">País</label>
              <input type="text" value={form.country} onChange={e => update('country', e.target.value)} className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-xl text-foreground-800 outline-none focus:border-foreground-300 transition-colors" />
            </div>
          </div>
          <div>
            <label className="text-foreground-500 text-[10px] font-semibold uppercase tracking-wide block mb-1">Radio Asignada</label>
            <input type="text" value={form.assignedRadio} onChange={e => update('assignedRadio', e.target.value)} placeholder="Nombre de la radio (ej: Radio Éxito FM)" className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-xl text-foreground-800 outline-none focus:border-foreground-300 transition-colors" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-foreground-400 text-[9px] block mb-1">Fecha Inicio</label>
              <input type="date" value={form.startDate} onChange={e => update('startDate', e.target.value)} className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-xl text-foreground-800 outline-none focus:border-foreground-300 transition-colors" />
            </div>
            <div>
              <label className="text-foreground-400 text-[9px] block mb-1">Fecha Fin</label>
              <input type="date" value={form.endDate} onChange={e => update('endDate', e.target.value)} className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-xl text-foreground-800 outline-none focus:border-foreground-300 transition-colors" />
            </div>
          </div>
        </div>
        <div className="sticky bottom-0 bg-background-50 border-t border-background-200 p-4 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium cursor-pointer hover:bg-background-200 transition-colors whitespace-nowrap">Cancelar</button>
          <button onClick={handleSubmit} disabled={saving || !form.companyName.trim()} className="px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold cursor-pointer hover:bg-foreground-800 transition-colors disabled:opacity-50 whitespace-nowrap">
            {saving ? 'Creando...' : 'Crear Patrocinador'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── View / Detail Modal ── */
function ViewSponsorModal({ sponsor, onClose }: { sponsor: Sponsor; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative bg-background-50 rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto shadow-lg">
        <div className="sticky top-0 bg-background-50 border-b border-background-200 p-5 flex items-start justify-between gap-4 z-10">
          <div className="flex items-center gap-4 min-w-0">
            <div className="w-12 h-12 rounded-xl bg-background-100 border border-background-200 flex items-center justify-center shrink-0">
              <i className={`${sectorIcons[sponsor.sector] ?? 'ri-building-4-line'} text-foreground-500 text-xl`}></i>
            </div>
            <div className="min-w-0">
              <h3 className="text-foreground-950 font-bold text-sm">{sponsor.companyName}</h3>
              <div className="flex items-center gap-2 mt-0.5">
                {typeBadge(sponsor.type)}
                {statusBadge(sponsor.status)}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center shrink-0 cursor-pointer hover:bg-background-200 transition-colors">
            <i className="ri-close-line text-foreground-500 text-sm"></i>
          </button>
        </div>
        <div className="p-5 space-y-5">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-background-100 rounded-xl p-3 text-center">
              <p className="text-foreground-400 text-[9px]">Sector</p>
              <p className="text-foreground-800 text-xs font-semibold mt-0.5">{sponsor.sector}</p>
            </div>
            <div className="bg-background-100 rounded-xl p-3 text-center">
              <p className="text-foreground-400 text-[9px]">Inversión Total</p>
              <p className="text-foreground-800 text-xs font-semibold mt-0.5">{formatCurrency(sponsor.totalInvestment)}</p>
            </div>
            <div className="bg-background-100 rounded-xl p-3 text-center">
              <p className="text-foreground-400 text-[9px]">Campañas</p>
              <p className="text-foreground-800 text-xs font-semibold mt-0.5">{sponsor.totalCampaigns}</p>
            </div>
            <div className="bg-background-100 rounded-xl p-3 text-center">
              <p className="text-foreground-400 text-[9px]">Radios Activas</p>
              <p className="text-foreground-800 text-xs font-semibold mt-0.5">{sponsor.activeRadios}</p>
            </div>
          </div>
          <div>
            <h4 className="text-foreground-500 text-[10px] font-semibold uppercase tracking-wide mb-2">Contacto</h4>
            <div className="bg-background-100 rounded-xl p-3 space-y-2">
              <div className="flex items-center gap-2 text-xs"><i className="ri-user-line text-foreground-400 text-sm w-5 h-5 flex items-center justify-center"></i><span className="text-foreground-800">{sponsor.contactName}</span></div>
              <div className="flex items-center gap-2 text-xs"><i className="ri-mail-line text-foreground-400 text-sm w-5 h-5 flex items-center justify-center"></i><span className="text-foreground-500">{sponsor.contactEmail}</span></div>
              <div className="flex items-center gap-2 text-xs"><i className="ri-phone-line text-foreground-400 text-sm w-5 h-5 flex items-center justify-center"></i><span className="text-foreground-500">{sponsor.contactPhone}</span></div>
              <div className="flex items-center gap-2 text-xs"><i className="ri-map-pin-line text-foreground-400 text-sm w-5 h-5 flex items-center justify-center"></i><span className="text-foreground-500">{sponsor.city}, {sponsor.country}</span></div>
              <div className="flex items-center gap-2 text-xs"><i className="ri-calendar-line text-foreground-400 text-sm w-5 h-5 flex items-center justify-center"></i><span className="text-foreground-500">Desde {sponsor.joinedDate}</span></div>
            </div>
          </div>
          {sponsor.campaigns.length > 0 && (
            <div>
              <h4 className="text-foreground-500 text-[10px] font-semibold uppercase tracking-wide mb-2">Campañas Recientes</h4>
              <div className="space-y-2">
                {sponsor.campaigns.slice(0, 4).map((c) => (
                  <div key={c.id} className="bg-background-100 rounded-xl p-3 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-foreground-800 text-xs font-medium line-clamp-1">{c.name}</p>
                      <p className="text-foreground-400 text-[9px] mt-0.5">{c.radioName} · {c.radioFrequency}</p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-foreground-800 text-xs font-semibold">{formatCurrency(c.investment)}</span>
                      <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-medium ${c.status === 'activo' ? 'bg-emerald-50 text-emerald-600' : c.status === 'programado' ? 'bg-amber-50 text-amber-600' : 'bg-foreground-100 text-foreground-500'}`}>
                        {c.status === 'activo' ? 'Activo' : c.status === 'programado' ? 'Programado' : 'Finalizado'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="sticky bottom-0 bg-background-50 border-t border-background-200 p-4 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium cursor-pointer hover:bg-background-200 transition-colors whitespace-nowrap">Cerrar</button>
        </div>
      </div>
    </div>
  );
}

/* ── Edit Sponsor Modal ── */
function EditSponsorModal({ sponsor, onClose, onSave }: { sponsor: Sponsor; onClose: () => void; onSave: (data: Partial<Sponsor>) => void }) {
  const [formData, setFormData] = useState({
    companyName: sponsor.companyName,
    type: sponsor.type,
    sector: sponsor.sector,
    contactName: sponsor.contactName,
    contactEmail: sponsor.contactEmail,
    contactPhone: sponsor.contactPhone,
    city: sponsor.city,
    country: sponsor.country,
  });
  const [saving, setSaving] = useState(false);
  const handleChange = (field: string, value: string) => setFormData(p => ({ ...p, [field]: value }));
  const handleSubmit = () => { setSaving(true); setTimeout(() => { onSave(formData); setSaving(false); onClose(); }, 600); };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative bg-background-50 rounded-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto shadow-lg">
        <div className="sticky top-0 bg-background-50 border-b border-background-200 p-5 flex items-center justify-between gap-4 z-10">
          <div><h3 className="text-foreground-950 font-bold text-sm">Editar Patrocinador</h3><p className="text-foreground-400 text-[10px] mt-0.5">{sponsor.companyName}</p></div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center shrink-0 cursor-pointer hover:bg-background-200 transition-colors"><i className="ri-close-line text-foreground-500 text-sm"></i></button>
        </div>
        <div className="p-5 space-y-4">
          <div><label className="text-foreground-500 text-[10px] font-semibold uppercase tracking-wide block mb-1">Empresa</label><input type="text" value={formData.companyName} onChange={e => handleChange('companyName', e.target.value)} className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-xl text-foreground-800 outline-none focus:border-foreground-300 transition-colors" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-foreground-500 text-[10px] font-semibold uppercase tracking-wide block mb-1">Tipo</label><select value={formData.type} onChange={e => handleChange('type', e.target.value)} className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-xl text-foreground-800 outline-none focus:border-foreground-300 cursor-pointer"><option value="local">Local</option><option value="regional">Regional</option><option value="nacional">Nacional</option></select></div>
            <div><label className="text-foreground-500 text-[10px] font-semibold uppercase tracking-wide block mb-1">Sector</label><input type="text" value={formData.sector} onChange={e => handleChange('sector', e.target.value)} className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-xl text-foreground-800 outline-none focus:border-foreground-300 transition-colors" /></div>
          </div>
          <div><label className="text-foreground-500 text-[10px] font-semibold uppercase tracking-wide block mb-1">Nombre Contacto</label><input type="text" value={formData.contactName} onChange={e => handleChange('contactName', e.target.value)} className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-xl text-foreground-800 outline-none focus:border-foreground-300 transition-colors" /></div>
          <div><label className="text-foreground-500 text-[10px] font-semibold uppercase tracking-wide block mb-1">Email</label><input type="email" value={formData.contactEmail} onChange={e => handleChange('contactEmail', e.target.value)} className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-xl text-foreground-800 outline-none focus:border-foreground-300 transition-colors" /></div>
          <div><label className="text-foreground-500 text-[10px] font-semibold uppercase tracking-wide block mb-1">Teléfono</label><input type="text" value={formData.contactPhone} onChange={e => handleChange('contactPhone', e.target.value)} className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-xl text-foreground-800 outline-none focus:border-foreground-300 transition-colors" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-foreground-500 text-[10px] font-semibold uppercase tracking-wide block mb-1">Ciudad</label><input type="text" value={formData.city} onChange={e => handleChange('city', e.target.value)} className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-xl text-foreground-800 outline-none focus:border-foreground-300 transition-colors" /></div>
            <div><label className="text-foreground-500 text-[10px] font-semibold uppercase tracking-wide block mb-1">País</label><input type="text" value={formData.country} onChange={e => handleChange('country', e.target.value)} className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-xl text-foreground-800 outline-none focus:border-foreground-300 transition-colors" /></div>
          </div>
        </div>
        <div className="sticky bottom-0 bg-background-50 border-t border-background-200 p-4 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium cursor-pointer hover:bg-background-200 transition-colors whitespace-nowrap">Cancelar</button>
          <button onClick={handleSubmit} disabled={saving || !formData.companyName.trim()} className="px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold cursor-pointer hover:bg-foreground-800 transition-colors disabled:opacity-50 whitespace-nowrap">{saving ? 'Guardando...' : 'Guardar Cambios'}</button>
        </div>
      </div>
    </div>
  );
}

/* ── Campaigns Modal ── */
function CampaignsModal({ sponsor, onClose }: { sponsor: Sponsor; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative bg-background-50 rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto shadow-lg">
        <div className="sticky top-0 bg-background-50 border-b border-background-200 p-5 flex items-center justify-between gap-4 z-10">
          <div><h3 className="text-foreground-950 font-bold text-sm">Campañas · {sponsor.companyName}</h3><p className="text-foreground-400 text-[10px] mt-0.5">{sponsor.totalCampaigns} campañas · {formatCurrency(sponsor.totalInvestment)} invertido</p></div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center shrink-0 cursor-pointer hover:bg-background-200 transition-colors"><i className="ri-close-line text-foreground-500 text-sm"></i></button>
        </div>
        <div className="p-5 space-y-3">
          {sponsor.campaigns.length === 0 ? (
            <div className="text-center py-10"><div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-background-100 flex items-center justify-center"><i className="ri-megaphone-line text-foreground-300 text-xl"></i></div><p className="text-foreground-500 text-sm font-medium">Sin campañas</p><p className="text-foreground-300 text-xs mt-1">Este patrocinador aún no tiene campañas registradas</p></div>
          ) : (
            sponsor.campaigns.map((c) => (
              <div key={c.id} className="bg-background-100 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1 min-w-0"><p className="text-foreground-800 text-xs font-semibold line-clamp-1">{c.name}</p><div className="flex items-center gap-2 mt-1"><span className="text-foreground-400 text-[9px] flex items-center gap-1"><i className="ri-radio-line text-[9px]"></i>{c.radioName}</span><span className="text-foreground-300 text-[9px]">{c.radioFrequency}</span></div><p className="text-foreground-400 text-[9px] mt-1">{c.startDate} → {c.endDate}</p></div>
                <div className="flex items-center gap-3 shrink-0"><span className="text-foreground-800 text-sm font-bold">{formatCurrency(c.investment)}</span><span className={`px-2 py-0.5 rounded-full text-[9px] font-medium ${c.status === 'activo' ? 'bg-emerald-50 text-emerald-600' : c.status === 'programado' ? 'bg-amber-50 text-amber-600' : 'bg-foreground-100 text-foreground-500'}`}>{c.status === 'activo' ? 'Activo' : c.status === 'programado' ? 'Programado' : 'Finalizado'}</span></div>
              </div>
            ))
          )}
        </div>
        <div className="sticky bottom-0 bg-background-50 border-t border-background-200 p-4 flex items-center justify-end gap-2"><button onClick={onClose} className="px-4 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium cursor-pointer hover:bg-background-200 transition-colors whitespace-nowrap">Cerrar</button></div>
      </div>
    </div>
  );
}

/* ── History Modal ── */
function HistoryModal({ sponsor, onClose }: { sponsor: Sponsor; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative bg-background-50 rounded-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto shadow-lg">
        <div className="sticky top-0 bg-background-50 border-b border-background-200 p-5 flex items-center justify-between gap-4 z-10"><div><h3 className="text-foreground-950 font-bold text-sm">Historial · {sponsor.companyName}</h3><p className="text-foreground-400 text-[10px] mt-0.5">{sponsor.history.length} eventos desde {sponsor.joinedDate}</p></div><button onClick={onClose} className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center shrink-0 cursor-pointer hover:bg-background-200 transition-colors"><i className="ri-close-line text-foreground-500 text-sm"></i></button></div>
        <div className="p-5">
          <div className="relative pl-8 space-y-6 before:content-[''] before:absolute before:left-3 before:top-2 before:bottom-2 before:w-px before:bg-background-200">
            {sponsor.history.map((entry, idx) => (
              <div key={entry.id} className="relative">
                <div className={`absolute -left-[1.65rem] top-1 w-3 h-3 rounded-full border-2 border-background-50 ${idx === 0 ? 'bg-accent-500' : 'bg-background-300'}`}></div>
                <div className="bg-background-100 rounded-xl p-3">
                  <div className="flex items-center justify-between gap-2"><p className="text-foreground-800 text-xs font-semibold">{entry.action}</p><span className="text-foreground-400 text-[9px] whitespace-nowrap">{entry.date}</span></div>
                  <p className="text-foreground-500 text-[10px] mt-1">{entry.description}</p>
                  {entry.amount && <p className="text-emerald-600 text-[10px] font-semibold mt-1">+{formatCurrency(entry.amount)}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="sticky bottom-0 bg-background-50 border-t border-background-200 p-4 flex items-center justify-end gap-2"><button onClick={onClose} className="px-4 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium cursor-pointer hover:bg-background-200 transition-colors whitespace-nowrap">Cerrar</button></div>
      </div>
    </div>
  );
}

/* ── SPONSOR CARD ── */
function SponsorCard({ sponsor, onView, onEdit, onCampaigns, onHistory, onActivate, onDeactivate, onArchive, onDelete }: {
  sponsor: Sponsor; onView: (s: Sponsor) => void; onEdit: (s: Sponsor) => void; onCampaigns: (s: Sponsor) => void;
  onHistory: (s: Sponsor) => void; onActivate: (s: Sponsor) => void; onDeactivate: (s: Sponsor) => void;
  onArchive: (s: Sponsor) => void; onDelete: (s: Sponsor) => void;
}) {
  const [showActions, setShowActions] = useState(false);

  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl p-4 hover:border-background-300 transition-all group space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-background-100 border border-background-200 flex items-center justify-center shrink-0">
            <i className={`${sectorIcons[sponsor.sector] ?? 'ri-building-4-line'} text-foreground-500 text-sm`}></i>
          </div>
          <div className="min-w-0">
            <p className="text-foreground-800 text-xs font-semibold line-clamp-1">{sponsor.companyName}</p>
            <p className="text-foreground-400 text-[9px] mt-0.5">{sponsor.sector}</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {typeBadge(sponsor.type)}
          {statusBadge(sponsor.status)}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-background-100 rounded-xl p-2.5 text-center"><p className="text-foreground-800 text-sm font-bold">{sponsor.totalCampaigns}</p><p className="text-foreground-400 text-[8px] flex items-center justify-center gap-0.5"><i className="ri-megaphone-line text-[8px]"></i>Campañas</p></div>
        <div className="bg-background-100 rounded-xl p-2.5 text-center"><p className="text-foreground-800 text-sm font-bold">{sponsor.activeRadios}</p><p className="text-foreground-400 text-[8px] flex items-center justify-center gap-0.5"><i className="ri-radio-line text-[8px]"></i>Radios</p></div>
        <div className="bg-background-100 rounded-xl p-2.5 text-center"><p className="text-foreground-800 text-sm font-bold">{formatCurrency(sponsor.totalInvestment)}</p><p className="text-foreground-400 text-[8px] flex items-center justify-center gap-0.5"><i className="ri-money-dollar-circle-line text-[8px]"></i>Inversión</p></div>
      </div>
      <div className="flex items-center justify-between pt-1 border-t border-background-100">
        <span className="text-foreground-400 text-[9px] flex items-center gap-1"><i className="ri-map-pin-line text-[9px]"></i>{sponsor.city}, {sponsor.country}</span>
        <div className="relative">
          <button onClick={() => setShowActions(!showActions)} className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors">
            <i className={`${showActions ? 'ri-close-line' : 'ri-more-2-line'} text-foreground-500 text-xs`}></i>
          </button>
          {showActions && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowActions(false)}></div>
              <div className="absolute right-0 bottom-full mb-1 z-20 bg-background-50 border border-background-200 rounded-xl shadow-lg py-1 min-w-[155px]">
                <button onClick={() => { setShowActions(false); onView(sponsor); }} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"><i className="ri-eye-line text-sm"></i>Ver Detalle</button>
                <button onClick={() => { setShowActions(false); onEdit(sponsor); }} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"><i className="ri-edit-line text-sm"></i>Editar</button>
                <button onClick={() => { setShowActions(false); onCampaigns(sponsor); }} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"><i className="ri-megaphone-line text-sm"></i>Campañas</button>
                <button onClick={() => { setShowActions(false); onHistory(sponsor); }} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"><i className="ri-history-line text-sm"></i>Historial</button>
                <div className="border-t border-background-100 my-1"></div>
                {sponsor.status === 'inactivo' || sponsor.status === 'pendiente' ? (
                  <button onClick={() => { setShowActions(false); onActivate(sponsor); }} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-emerald-600 hover:bg-emerald-50 transition-colors cursor-pointer whitespace-nowrap"><i className="ri-play-circle-line text-sm"></i>Activar</button>
                ) : (
                  <button onClick={() => { setShowActions(false); onDeactivate(sponsor); }} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-amber-600 hover:bg-amber-50 transition-colors cursor-pointer whitespace-nowrap"><i className="ri-pause-circle-line text-sm"></i>Desactivar</button>
                )}
                <button onClick={() => { setShowActions(false); onArchive(sponsor); }} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"><i className="ri-archive-line text-sm"></i>Archivar</button>
                <button onClick={() => { setShowActions(false); onDelete(sponsor); }} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-500 hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap"><i className="ri-delete-bin-line text-sm"></i>Eliminar</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Loading Skeleton ── */
function LoadingSkeleton() {
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        {Array.from({ length: 6 }).map((_, i) => (<div key={i} className="bg-background-50 border border-background-200 rounded-2xl p-4 flex items-center gap-3 animate-pulse"><div className="w-10 h-10 rounded-xl bg-background-200"></div><div className="space-y-1"><div className="w-16 h-2.5 bg-background-200 rounded"></div><div className="w-10 h-4 bg-background-200 rounded"></div></div></div>))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {Array.from({ length: 9 }).map((_, i) => (<div key={i} className="bg-background-50 border border-background-200 rounded-2xl p-4 space-y-3 animate-pulse"><div className="flex items-center gap-3"><div className="w-10 h-10 rounded-xl bg-background-200"></div><div className="space-y-1 flex-1"><div className="w-2/3 h-3 bg-background-200 rounded"></div><div className="w-1/3 h-2 bg-background-200 rounded"></div></div></div><div className="grid grid-cols-3 gap-2">{Array.from({ length: 3 }).map((_, j) => (<div key={j} className="h-12 bg-background-200 rounded-xl"></div>))}</div><div className="flex justify-between"><div className="w-20 h-2.5 bg-background-200 rounded"></div><div className="w-7 h-7 bg-background-200 rounded-lg"></div></div></div>))}
      </div>
    </div>
  );
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (<div className="bg-background-50 border border-background-200 rounded-2xl p-12 text-center"><div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-red-50 flex items-center justify-center"><i className="ri-error-warning-line text-red-400 text-2xl"></i></div><p className="text-foreground-800 text-sm font-semibold mb-1">Error al cargar</p><p className="text-foreground-400 text-xs mb-4">{message}</p><button onClick={onRetry} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer"><i className="ri-refresh-line text-sm"></i>Reintentar</button></div>);
}

function EmptyState({ onClear }: { onClear: () => void }) {
  return (<div className="bg-background-50 border border-background-200 rounded-2xl p-12 text-center"><div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center"><i className="ri-hand-heart-line text-foreground-300 text-xl"></i></div><p className="text-foreground-500 text-sm font-medium">Sin resultados</p><p className="text-foreground-300 text-xs mt-1">No hay patrocinadores que coincidan con los filtros</p><button onClick={onClear} className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background-100 text-foreground-500 text-[10px] font-medium hover:bg-background-200 transition-colors cursor-pointer"><i className="ri-filter-off-line text-xs"></i>Limpiar filtros</button></div>);
}

function SummaryBar() {
  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl p-3.5 flex flex-wrap items-center gap-4">
      <div className="flex items-center gap-3"><div className="w-8 h-8 rounded-lg bg-accent-50 flex items-center justify-center"><i className="ri-hand-heart-line text-accent-500 text-sm"></i></div><div><p className="text-foreground-400 text-[9px]">Total Patrocinadores</p><p className="text-foreground-800 text-sm font-bold">{sponsorMetrics.totalSponsors}</p></div></div>
      <div className="w-px h-8 bg-background-200 hidden sm:block"></div>
      <div className="flex items-center gap-3"><div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center"><i className="ri-check-line text-emerald-500 text-sm"></i></div><div><p className="text-foreground-400 text-[9px]">Activos</p><p className="text-foreground-800 text-sm font-bold">{sponsorMetrics.activeSponsors}</p></div></div>
      <div className="w-px h-8 bg-background-200 hidden sm:block"></div>
      <div className="flex items-center gap-3"><div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center"><i className="ri-money-dollar-circle-line text-amber-500 text-sm"></i></div><div><p className="text-foreground-400 text-[9px]">Inversión Total</p><p className="text-foreground-800 text-sm font-bold">{formatCurrency(sponsorMetrics.totalInvestment)}</p></div></div>
      <div className="w-px h-8 bg-background-200 hidden sm:block"></div>
      <div className="flex items-center gap-3"><div className="w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center"><i className="ri-megaphone-line text-violet-500 text-sm"></i></div><div><p className="text-foreground-400 text-[9px]">Campañas Totales</p><p className="text-foreground-800 text-sm font-bold">{sponsorMetrics.totalCampaigns}</p></div></div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════ */

let nextSponsorId = 100;

export default function SASponsors() {
  const [sponsorList, setSponsorList] = useState<Sponsor[]>([]);
  const [loadingState, setLoadingState] = useState<LoadingState>('loading');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<SponsorType | 'todos'>('todos');
  const [statusFilter, setStatusFilter] = useState<SponsorStatus | 'todos'>('todos');
  const [searchTerm, setSearchTerm] = useState('');
  const [inlineMsg, setInlineMsg] = useState<string | null>(null);

  const [viewSponsor, setViewSponsor] = useState<Sponsor | null>(null);
  const [editSponsor, setEditSponsor] = useState<Sponsor | null>(null);
  const [campaignSponsor, setCampaignSponsor] = useState<Sponsor | null>(null);
  const [historySponsor, setHistorySponsor] = useState<Sponsor | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const showInline = (m: string) => { setInlineMsg(m); setTimeout(() => setInlineMsg(null), 3500); };

  const loadData = useCallback(() => {
    setLoadingState('loading'); setErrorMsg(null);
    simulateLoad(sponsors).then(data => { setSponsorList(data); setLoadingState('ready'); }).catch(err => { setErrorMsg(err.message || 'Error desconocido'); setLoadingState('error'); });
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  /* ── Computed ── */
  const filteredSponsors = useMemo(() => {
    let result = sponsorList;
    if (typeFilter !== 'todos') result = result.filter(s => s.type === typeFilter);
    if (statusFilter !== 'todos') result = result.filter(s => s.status === statusFilter);
    if (searchTerm.trim()) { const q = searchTerm.toLowerCase(); result = result.filter(s => s.companyName.toLowerCase().includes(q) || s.sector.toLowerCase().includes(q) || s.contactName.toLowerCase().includes(q) || s.city.toLowerCase().includes(q) || s.country.toLowerCase().includes(q)); }
    return result;
  }, [sponsorList, typeFilter, statusFilter, searchTerm]);

  const filteredMetrics = useMemo(() => ({
    total: filteredSponsors.length,
    investment: filteredSponsors.reduce((s, sp) => s + sp.totalInvestment, 0),
    campaigns: filteredSponsors.reduce((s, sp) => s + sp.totalCampaigns, 0),
    radios: filteredSponsors.reduce((s, sp) => s + sp.activeRadios, 0),
  }), [filteredSponsors]);

  const typeCounts = useMemo(() => ({
    todos: sponsorList.length, local: sponsorList.filter(s => s.type === 'local').length, regional: sponsorList.filter(s => s.type === 'regional').length, nacional: sponsorList.filter(s => s.type === 'nacional').length,
  }), [sponsorList]);

  const statusCounts = useMemo(() => ({
    todos: sponsorList.length, activo: sponsorList.filter(s => s.status === 'activo').length, inactivo: sponsorList.filter(s => s.status === 'inactivo').length, pendiente: sponsorList.filter(s => s.status === 'pendiente').length,
  }), [sponsorList]);

  /* ── Actions ── */
  const handleEditSave = useCallback((data: Partial<Sponsor>) => {
    if (!editSponsor) return;
    setSponsorList(prev => prev.map(s => s.id === editSponsor.id ? { ...s, ...data } : s));
    showInline(`"${editSponsor.companyName}" actualizado`);
  }, [editSponsor]);

  const handleCreate = useCallback((data: SponsorFormData) => {
    const newSponsor: Sponsor = {
      id: nextSponsorId++,
      companyName: data.companyName,
      type: data.type,
      totalCampaigns: 0,
      activeRadios: data.assignedRadio ? 1 : 0,
      totalInvestment: 0,
      status: 'pendiente',
      sector: data.sector || 'General',
      contactName: data.contactName,
      contactEmail: data.contactEmail,
      contactPhone: data.contactPhone,
      city: data.city,
      country: data.country,
      joinedDate: data.startDate || new Date().toISOString().slice(0, 10),
      campaigns: [],
      history: [{ id: Date.now(), action: 'Registro', date: new Date().toISOString().slice(0, 10), description: 'Alta como nuevo patrocinador.' }],
    };
    setSponsorList(prev => [...prev, newSponsor]);
    Swal.fire({ icon: 'success', title: 'Patrocinador creado', text: `"${data.companyName}" se agregó correctamente.`, timer: 2000, showConfirmButton: false, customClass: { popup: 'rounded-2xl text-sm' } });
    showInline(`"${data.companyName}" creado`);
  }, []);

  const handleActivate = useCallback((s: Sponsor) => {
    swalConfirm('¿Activar patrocinador?', `"${s.companyName}" pasará a estado Activo.`, 'Activar', '#10b981').then(ok => {
      if (ok) { setSponsorList(prev => prev.map(sp => sp.id === s.id ? { ...sp, status: 'activo' as const } : sp)); showInline(`"${s.companyName}" activado`); }
    });
  }, []);

  const handleDeactivate = useCallback((s: Sponsor) => {
    swalConfirm('¿Desactivar patrocinador?', `"${s.companyName}" pasará a estado Inactivo.`, 'Desactivar', '#f59e0b').then(ok => {
      if (ok) { setSponsorList(prev => prev.map(sp => sp.id === s.id ? { ...sp, status: 'inactivo' as const } : sp)); showInline(`"${s.companyName}" desactivado`); }
    });
  }, []);

  const handleArchive = useCallback((s: Sponsor) => {
    swalConfirm('¿Archivar patrocinador?', `"${s.companyName}" se archivará. No aparecerá en los listados activos.`, 'Archivar', '#6b7280').then(ok => {
      if (ok) { setSponsorList(prev => prev.map(sp => sp.id === s.id ? { ...sp, status: 'inactivo' as const } : sp)); showInline(`"${s.companyName}" archivado`); }
    });
  }, []);

  const handleDelete = useCallback((s: Sponsor) => {
    swalConfirm('¿Eliminar patrocinador?', `"${s.companyName}" se eliminará permanentemente. Esta acción no se puede deshacer.`, 'Eliminar', '#ef4444').then(ok => {
      if (ok) { setSponsorList(prev => prev.filter(sp => sp.id !== s.id)); showInline(`"${s.companyName}" eliminado`); }
    });
  }, []);

  const clearAllFilters = () => { setTypeFilter('todos'); setStatusFilter('todos'); setSearchTerm(''); };

  return (
    <div className="min-h-screen bg-background-100">
      <div className="flex">
        <SASidebar />
        <main className="flex-1 min-w-0">
          <div className="p-4 md:p-6 lg:p-8 pb-8 space-y-5 max-w-[1600px] mx-auto">
            <div className="flex items-center gap-1.5 text-[10px] text-foreground-400"><Link to="/sa/dashboard" className="hover:text-foreground-600 transition-colors cursor-pointer">Dashboard</Link><i className="ri-arrow-right-s-line text-[9px]"></i><span className="text-foreground-500 font-medium">Patrocinadores</span></div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-[10px] font-semibold mb-2"><i className="ri-hand-heart-line text-xs"></i>Bloque SA-14</div>
                <h2 className="text-foreground-950 font-bold text-lg">Patrocinadores</h2>
                <p className="text-foreground-400 text-xs mt-0.5">Gestión de patrocinadores · Local, Regional y Nacional · {loadingState === 'ready' ? `${sponsorList.length} patrocinadores` : 'Cargando...'}</p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold cursor-pointer hover:bg-foreground-800 transition-colors whitespace-nowrap"><i className="ri-add-line text-sm"></i>Crear Sponsor</button>
                <div className="relative w-full sm:w-56">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-xs"></i>
                  <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Buscar patrocinador, ciudad..." disabled={loadingState !== 'ready'} className="w-full pl-8 pr-8 py-2 bg-background-50 border border-background-200 rounded-xl text-xs text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors disabled:opacity-50" />
                  {searchTerm && <button onClick={() => setSearchTerm('')} className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 rounded-md bg-background-200 flex items-center justify-center cursor-pointer hover:bg-background-300"><i className="ri-close-line text-foreground-500 text-[10px]"></i></button>}
                </div>
              </div>
            </div>

            {inlineMsg && <div className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-50 text-emerald-700 text-[10px] font-medium w-fit"><i className="ri-check-line text-emerald-500 text-xs"></i>{inlineMsg}</div>}

            {loadingState === 'ready' && <SummaryBar />}

            {loadingState === 'ready' && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                <KpiCard label="Total Patrocinadores" value={String(filteredMetrics.total)} icon="ri-hand-heart-line" bg="bg-accent-50" color="text-accent-500" />
                <KpiCard label="Inversión Total" value={formatCurrency(filteredMetrics.investment)} icon="ri-money-dollar-circle-line" bg="bg-emerald-50" color="text-emerald-500" />
                <KpiCard label="Campañas" value={String(filteredMetrics.campaigns)} icon="ri-megaphone-line" bg="bg-violet-50" color="text-violet-500" />
                <KpiCard label="Radios Activas" value={String(filteredMetrics.radios)} icon="ri-radio-line" bg="bg-sky-50" color="text-sky-500" />
                <KpiCard label="Locales" value={String(sponsorMetrics.byType.local.count)} icon="ri-map-pin-line" bg="bg-amber-50" color="text-amber-500" />
                <KpiCard label="Nacionales" value={String(sponsorMetrics.byType.nacional.count)} icon="ri-global-line" bg="bg-primary-50" color="text-primary-500" />
              </div>
            )}

            {loadingState === 'loading' && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                {Array.from({ length: 6 }).map((_, i) => (<div key={i} className="bg-background-50 border border-background-200 rounded-2xl p-4 flex items-center gap-3 animate-pulse"><div className="w-10 h-10 rounded-xl bg-background-200"></div><div className="space-y-1"><div className="w-16 h-2.5 bg-background-200 rounded"></div><div className="w-10 h-4 bg-background-200 rounded"></div></div></div>))}
              </div>
            )}

            {loadingState === 'ready' && (
              <div className="space-y-3">
                <div className="flex items-center gap-1.5 flex-wrap"><span className="text-foreground-400 text-[9px] font-medium mr-1">Tipo:</span><TypeChip type="todos" active={typeFilter} onClick={setTypeFilter} count={typeCounts.todos} /><TypeChip type="local" active={typeFilter} onClick={setTypeFilter} count={typeCounts.local} /><TypeChip type="regional" active={typeFilter} onClick={setTypeFilter} count={typeCounts.regional} /><TypeChip type="nacional" active={typeFilter} onClick={setTypeFilter} count={typeCounts.nacional} /></div>
                <div className="flex items-center gap-1.5 flex-wrap"><span className="text-foreground-400 text-[9px] font-medium mr-1">Estado:</span>
                  {(['todos','activo','pendiente','inactivo'] as const).map(st => (
                    <button key={st} onClick={() => setStatusFilter(st)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${statusFilter === st ? 'bg-foreground-950 text-background-50' : 'bg-background-50 text-foreground-500 border border-background-200 hover:border-foreground-300'}`}>
                      {st === 'todos' ? 'Todos' : st === 'activo' ? 'Activos' : st === 'pendiente' ? 'Pendientes' : 'Inactivos'}
                      <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold ${statusFilter === st ? 'bg-background-50/20 text-background-50' : 'bg-background-100 text-foreground-400'}`}>{statusCounts[st]}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {loadingState === 'loading' && <LoadingSkeleton />}
            {loadingState === 'error' && <ErrorState message={errorMsg || 'Error'} onRetry={loadData} />}
            {loadingState === 'ready' && filteredSponsors.length === 0 && <EmptyState onClear={clearAllFilters} />}
            {loadingState === 'ready' && filteredSponsors.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {filteredSponsors.map(sponsor => (
                  <SponsorCard key={sponsor.id} sponsor={sponsor} onView={setViewSponsor} onEdit={setEditSponsor} onCampaigns={setCampaignSponsor} onHistory={setHistorySponsor} onActivate={handleActivate} onDeactivate={handleDeactivate} onArchive={handleArchive} onDelete={handleDelete} />
                ))}
              </div>
            )}
          </div>
        </main>
      </div>
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-background-50/98 backdrop-blur-2xl border-t border-background-200 py-3 px-4 safe-bottom"><p className="text-center text-foreground-400 text-[10px] font-medium">Super Admin · Patrocinadores</p></div>
      {viewSponsor && <ViewSponsorModal sponsor={viewSponsor} onClose={() => setViewSponsor(null)} />}
      {editSponsor && <EditSponsorModal sponsor={editSponsor} onClose={() => setEditSponsor(null)} onSave={handleEditSave} />}
      {campaignSponsor && <CampaignsModal sponsor={campaignSponsor} onClose={() => setCampaignSponsor(null)} />}
      {historySponsor && <HistoryModal sponsor={historySponsor} onClose={() => setHistorySponsor(null)} />}
      {showCreate && <CreateSponsorModal onClose={() => setShowCreate(false)} onSave={handleCreate} />}
    </div>
  );
}